import { NotFoundError } from '@/modules/errors'
import { executePost } from '@/services/lukita/rest'
import { captureException } from '@/services/monitoring/captureException'

export type UnsubscribeOutcome = 'unsubscribed' | 'linkNotValid' | 'failed'

const OPT_OUT_ROUTE = '/email_opt_out.json'
const OPT_OUT_TIMEOUT_MS = 15_000

// Only an explicit success from lukita counts: a 200 from anything else must never read as unsubscribed.
const isUnsubscribedAnswer = (answer: unknown): boolean =>
  typeof answer === 'object' &&
  answer !== null &&
  (answer as { unsubscribed?: unknown }).unsubscribed === true

const readAnswer = async (
  response: Response | undefined,
): Promise<UnsubscribeOutcome> => {
  if (!response) throw new Error('The unsubscribe request timed out.')

  const answer: unknown = await response.json()
  if (!isUnsubscribedAnswer(answer)) {
    throw new Error(`Unexpected unsubscribe answer: ${JSON.stringify(answer)}`)
  }
  return 'unsubscribed'
}

// The timer stays on until the body is read, so a stalled answer also ends in a failure.
const requestOptOut = async (token: string): Promise<UnsubscribeOutcome> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), OPT_OUT_TIMEOUT_MS)
  try {
    const response: Response | undefined = await executePost(
      OPT_OUT_ROUTE,
      undefined,
      {
        queryParams: { token },
        aborter: { abortController: () => controller },
      },
    )
    return await readAnswer(response)
  } finally {
    clearTimeout(timer)
  }
}

const outcomeOfError = (error: unknown): UnsubscribeOutcome => {
  if (error instanceof NotFoundError) return 'linkNotValid'

  captureException(error instanceof Error ? error : new Error(String(error)))
  return 'failed'
}

export const unsubscribeFromReminderEmails = async (
  token: string,
): Promise<UnsubscribeOutcome> => {
  if (!token.trim()) return 'linkNotValid'

  try {
    return await requestOptOut(token)
  } catch (error) {
    return outcomeOfError(error)
  }
}
